import { ReactElement, ElementType, useCallback, useMemo } from 'react';
import { DialogTitle, Button } from '@mui/material';
import { TabContext } from '@mui/lab';
import { LoadingButton } from '@mui/lab';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';
import moment from 'moment';

import { IntegrationDeliveryTypes } from '~globals/types/enums';
import { formatterDate } from '~utils/index';
import { useSelectedRows } from '~hooks/useSelectedRows';
import { useTypePartnerCompany } from '~hooks/useTypePartnerCompany';
import { AdminIntegration } from '~services/admin/integration/types';
import {
  ItemImportFromIntegrationsBody,
  ItemImportFromIntegrationsData,
} from '~services/item/types';

import {
  FIELDS_NAME,
  getInitialValues,
  validationSchema,
  INTEGRATION_ID_KEY,
} from './utils';
import { DialogImportOrdersFromIntegrationProps } from './types';
import {
  DialogImportOrdersFromIntegrationContextPanelsId,
  useDialogImportOrdersFromIntegrationContext,
} from './DialogImportOrdersFromIntegrationContext';
// eslint-disable-next-line max-len
import DialogImportOrdersFromIntegrationTableProvider from './DialogImportOrdersFromIntegrationTableContext';
import {
  DialogImportOrdersFromIntegrationNotIntegrations,
  DialogImportOrdersFromIntegrationPresentation,
  DialogImportOrdersFromIntegrationProgress,
} from './Tabs';
import {
  DialogImportOrdersFromIntegrationContentContainer,
  DialogImportOrdersFromIntegrationTabPanel,
  DialogImportOrdersFromIntegrationActions,
} from './styles';

interface DialogImportOrdersFromIntegrationTabPanelItem {
  id: DialogImportOrdersFromIntegrationContextPanelsId;
  panel: ElementType;
}

const TABS_PANELS: DialogImportOrdersFromIntegrationTabPanelItem[] = [
  {
    id: 'notIntegrations',
    panel: DialogImportOrdersFromIntegrationNotIntegrations,
  },
  { id: 'presentation', panel: DialogImportOrdersFromIntegrationPresentation },
  { id: 'progress', panel: DialogImportOrdersFromIntegrationProgress },
];

const DialogImportOrdersFromIntegrationContent = ({
  onClose,
}: Pick<DialogImportOrdersFromIntegrationProps, 'onClose'>): ReactElement => {
  const { isCarrier } = useTypePartnerCompany();

  const {
    activeTab,
    currentIntegrations: { list: currentIntegrationsData },
    importerRequest: {
      executeImport: handleImport,
      loading: loadingExecuteImportItems,
    },
  } = useDialogImportOrdersFromIntegrationContext();

  const defaultTableSelectedIds = useMemo<string[]>(
    () => currentIntegrationsData.map((row) => row[INTEGRATION_ID_KEY]),
    [currentIntegrationsData],
  );

  const {
    selectedRowsIds: tableSelectedIds,
    handleOnSelectAll: tableHandleOnSelectAll,
    handleOnSelect: tableHandleOnSelect,
  } = useSelectedRows<AdminIntegration>(
    INTEGRATION_ID_KEY,
    currentIntegrationsData,
    defaultTableSelectedIds,
  );

  const onSubmit = useCallback(
    async (
      values: ReturnType<typeof getInitialValues>,
      { setSubmitting }: FormikHelpers<ReturnType<typeof getInitialValues>>,
    ) => {
      const integrationsData: ItemImportFromIntegrationsData[] = values[
        FIELDS_NAME.INTEGRATIONS
      ]
        .filter((integration) =>
          tableSelectedIds.includes(integration[FIELDS_NAME.INTEGRATION_ID]),
        )
        .map((integration) => {
          const deliveryTypes = isCarrier
            ? integration[FIELDS_NAME.INTEGRATION_DELIVERY_TYPE]
            : [IntegrationDeliveryTypes.SelfService];

          return {
            companyIntegrationId: integration[FIELDS_NAME.INTEGRATION_ID],
            deliveryTypes,
          };
        });

      const zonesId = isCarrier ? values[FIELDS_NAME.ZONES_ID] : null;

      const body: ItemImportFromIntegrationsBody = {
        dateFrom: formatterDate(values[FIELDS_NAME.DATE_FROM], {
          format: moment.HTML5_FMT.DATETIME_LOCAL_MS,
        }),
        dateTo: formatterDate(values[FIELDS_NAME.DATE_TO], {
          format: moment.HTML5_FMT.DATETIME_LOCAL_MS,
        }),
        zonesId,
        integrations: integrationsData,
        onyTodayDelivery: values[FIELDS_NAME.ONLY_TODAY_DELIVERY],
      };

      await handleImport(body);

      setSubmitting(false);
    },
    [isCarrier, handleImport, tableSelectedIds],
  );

  const formikBag = useFormik({
    initialValues: getInitialValues(currentIntegrationsData),
    validationSchema,
    onSubmit,
  });

  const { submitForm, isSubmitting } = formikBag;

  return (
    <FormikProvider value={formikBag}>
      <DialogImportOrdersFromIntegrationTableProvider
        selectableProps={{
          selectedIds: tableSelectedIds,
          onSelectAll: tableHandleOnSelectAll,
          onSelect: tableHandleOnSelect,
        }}
      >
        <DialogTitle>Importar pedidos desde tiendas</DialogTitle>

        <DialogImportOrdersFromIntegrationContentContainer dividers>
          <TabContext value={activeTab}>
            {TABS_PANELS.map(({ id, panel: Panel }) => (
              <DialogImportOrdersFromIntegrationTabPanel
                key={`dialog-import-orders-from-integration-panel-${id}`}
                value={id}
              >
                <Panel />
              </DialogImportOrdersFromIntegrationTabPanel>
            ))}
          </TabContext>
        </DialogImportOrdersFromIntegrationContentContainer>

        <DialogImportOrdersFromIntegrationActions>
          <Button color="secondary" onClick={onClose}>
            Cerrar
          </Button>

          {activeTab === 'presentation' && (
            <LoadingButton
              variant="contained"
              color="primary"
              onClick={submitForm}
              loading={loadingExecuteImportItems || isSubmitting}
              disabled={tableSelectedIds.length === 0}
            >
              Importar
            </LoadingButton>
          )}
        </DialogImportOrdersFromIntegrationActions>
      </DialogImportOrdersFromIntegrationTableProvider>
    </FormikProvider>
  );
};

export default DialogImportOrdersFromIntegrationContent;
